import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { useAuth, useAuthFeatures } from "@/context/AuthContext";
import { useOptions } from "@/hooks/useDirectory";
import { OptionPicker } from "@/components/common/OptionPicker";
import { PageHeader } from "@/components/common/Layout";
import { Field, FormSection } from "@/components/common/Detail";
import { LoadingGrid, LoginPrompt } from "@/components/common/States";
import { cn } from "@/lib/utils";

const EMPTY = {
  name: "", logo_url: "", org_type: [], sector: [], relation_types: [], website: "", address: "", department: [],
  siret: "", description: "", expertises: [], main_contact: "none",
};

export default function OrgFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const tenantMode = useLocation().pathname.startsWith("/locataires");
  const base = tenantMode ? "/locataires" : "/entreprises";
  const { isAuthed, loading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(tenantMode && !editing ? { ...EMPTY, relation_types: ["Locataire"] } : EMPTY);
  const [initialLogo, setInitialLogo] = useState("");
  const [error, setError] = useState("");
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);

  const org = useQuery({ queryKey: ["organization", id, true], queryFn: async () => (await api.get(`/organizations/${id}`)).data, enabled: editing && isAuthed });
  const contacts = useQuery({ queryKey: ["org-contacts", id, true], queryFn: async () => (await api.get(`/organizations/${id}/contacts`)).data, enabled: editing && isAuthed });
  const options = useOptions(isAuthed);
  const features = useAuthFeatures();

  useEffect(() => {
    const o = org.data;
    if (!o) return;
    setForm({
      name: o.name || "", logo_url: o.logo || "", org_type: o.org_type, sector: o.sector,
      relation_types: o.relation_types || [], website: o.website || "", address: o.address || "", department: o.department,
      siret: o.siret || "", description: o.description || "", expertises: o.expertises, main_contact: o.main_contact_ids?.[0] || "none",
    });
    setInitialLogo(o.logo || "");
  }, [org.data]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));
    if (key === "name") { setError(""); setExisting(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError("Le nom est obligatoire."); return; }
    setSaving(true);
    const { main_contact, ...payload } = form;
    if (editing && features.org_main_contact) payload.main_contact_ids = main_contact === "none" ? [] : [main_contact];
    if (!features.org_expertises) delete payload.expertises;
    if (editing && payload.logo_url === initialLogo) delete payload.logo_url;
    try {
      const { data } = editing ? await api.patch(`/organizations/${id}`, payload) : await api.post("/organizations", payload);
      toast.success(editing ? "Organisation mise à jour dans Notion" : "Organisation créée dans Notion");
      queryClient.invalidateQueries();
      navigate(`${base}/${data.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.existing) { setExisting(detail.existing); setError(detail.message); }
      else toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!isAuthed) return <div className="lm-container py-12"><LoginPrompt title="Ajout et modification réservés aux membres de La Mêlée" /></div>;
  if (editing && org.isLoading) return <div className="lm-container py-12"><LoadingGrid count={2} /></div>;

  const text = (key, props = {}) => <Input id={key} value={form[key]} onChange={set(key)} data-testid={`org-form-${key.replace(/_/g, "-")}-input`} {...props} />;
  const o = options.data?.organizations || {};
  const pick = (key, single = false) => <OptionPicker single={single} options={o[key]} value={form[key]} onChange={set(key)} testId={`org-form-${key.replace(/_/g, "-")}`} />;

  return (
    <form onSubmit={submit} className="lm-container max-w-4xl pb-32" data-testid="org-form" noValidate>
      <PageHeader eyebrow={editing ? "Modification" : "Nouvelle organisation"} title={editing ? `Modifier ${org.data?.name || ""}` : tenantMode ? "Ajouter un locataire" : "Ajouter une entreprise"}
        description="Les informations sont enregistrées directement dans la base Notion Maisonna." />
      <div className="space-y-6">
        <FormSection title="Identité">
          <Field label="Nom de la structure" required htmlFor="name" error={error} className="sm:col-span-2">
            {text("name", { className: cn(error && "border-red-400") })}
            {existing && (
              <Link to={`${base}/${existing.id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-primary hover:underline" data-testid="org-form-existing-link">
                <Building2 className="h-4 w-4" /> Ouvrir « {existing.name} »
              </Link>
            )}
          </Field>
          <Field label="Type d'organisation" htmlFor="org_type" className="sm:col-span-2">{pick("org_type")}</Field>
          <Field label="Secteur d'activité" htmlFor="sector" className="sm:col-span-2">{pick("sector")}</Field>
          <Field label="Description" htmlFor="description" className="sm:col-span-2">{text("description")}</Field>
          <Field label="Logo (URL)" htmlFor="logo_url">{text("logo_url", { placeholder: "https://…" })}</Field>
          <Field label="Site internet" htmlFor="website">{text("website", { placeholder: "https://…" })}</Field>
          <Field label="SIRET" htmlFor="siret">{text("siret")}</Field>
        </FormSection>
        <FormSection title="Localisation">
          <Field label="Adresse" htmlFor="address" className="sm:col-span-2">{text("address")}</Field>
          <Field label="Département" htmlFor="department" className="sm:col-span-2">{pick("department")}</Field>
        </FormSection>
        <section className="lm-card space-y-6 p-6 sm:p-7">
          <h2 className="text-lg font-bold">Relation avec La Mêlée</h2>
          <Field label="Relation La Mêlée" htmlFor="relation_types">{pick("relation_types")}</Field>
          <Field label="Expertises" htmlFor="expertises">{features.org_expertises ? pick("expertises") : <p className="text-xs text-muted-foreground">Propriété « Expertises » absente de Maisonna.</p>}</Field>
          {features.org_main_contact && (
          <Field label="Contact principal" htmlFor="main_contact"
            hint={editing ? "Choisi parmi les personnes rattachées à cette organisation." : "Disponible après la création, une fois des contacts rattachés."}>
            <Select value={form.main_contact} onValueChange={set("main_contact")} disabled={!editing || !contacts.data?.items.length}>
              <SelectTrigger data-testid="org-form-main-contact-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {(contacts.data?.items || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name}{p.job_title ? ` — ${p.job_title}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          )}
        </section>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-white/90 backdrop-blur-xl">
        <div className="lm-container flex max-w-4xl items-center justify-end gap-3 py-3">
          <Button type="button" variant="ghost" onClick={() => navigate(-1)}>Annuler</Button>
          <Button type="submit" disabled={saving} className="rounded-full px-6 font-semibold" data-testid="org-form-submit-button">
            {saving ? "Enregistrement…" : editing ? "Enregistrer les modifications" : "Créer l'organisation"}
          </Button>
        </div>
      </div>
    </form>
  );
}
